import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import {
  AdsFilteringType,
  AdModel,
  AdsSortingType,
  AdsViewModeEnum,
  NotificationTypeEnum,
  State,
  TopAgentListItem,
  UserKindEnum,
} from '@/types';
import { setAddressParamsInFilteringObj } from '@/utils';
import { useChangeQueryParams } from '@/hooks';
import { Loader, MyButton, Pagination } from '@/components';
import { adsProvider, savedFilterProvider } from '@/providers';
import { appSetNotification } from '@/store';

import { COUNT_ADS_PER_PAGE } from './constants';
import { AdsFilters } from './AdsFilters/AdsFilters';
import { getPreservedLocationDisplayParams } from './AdsFilters/locationFilter';
import { AdsListControls, ResetPublicationTimerAction } from './AdsListControls';
import { AdsResultsCountBadge } from './AdsResultsCountBadge';
import { EmptyList } from './EmptyList';
import { AdsListItem, DialogSelection } from './AdsListItem';
import { SaveFilterDialog, SaveFilterDialogState, SaveFilterResult } from './SaveFilterDialog';
import SaveFilterIcon from '@mui/icons-material/Save';
import styles from './Ads.module.scss';
import { MapUi } from '@/components/Map/Map';
import { useLocationAutocomplete } from '@/components/LocationAutocomplete';
import { useUserSettings } from '@/hooks/useUserSettings';
import {
  parseAdsSearchParams,
  stringifyAdsSearchParams,
  type TopAgentFilterValue,
} from './utils/adsSearchParams';
import { normalizeAdsFilteringObject } from './utils/normalizeAdsFilteringObject';
import { useAdsStorefrontData } from './useAdsStorefrontData';
import {
  buildAppliedAdsFilterSnapshot,
  hasMeaningfulAppliedFilters,
} from './utils/appliedAdsFilterSnapshot';
import {
  buildStorefrontClientRequestRouteState,
  StorefrontClientRequestRouteState,
} from './utils/clientRequestRouteState';
import type { AdsFilterChangeOptions } from './AdsFilters/filterTypes';

interface AdsProps {
  adsPerPage?: number;
  enableAmbassadorPromotion?: boolean;
  enableTopAgentFilter?: boolean;
  isRent?: boolean;
}

export const Ads = ({
  adsPerPage = COUNT_ADS_PER_PAGE,
  enableAmbassadorPromotion = false,
  enableTopAgentFilter = false,
  isRent,
}: AdsProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector(({ auth }: State) => auth);
  const changeQueryParams = useChangeQueryParams(isRent ? 'ads/rent' : '');
  const location = useLocation();
  const queryParamsStr = location.search;
  const queryParams = parseAdsSearchParams(queryParamsStr);
  const addressInputValue = queryParams.addressParamName || queryParams.addressParamAddress || '';
  const appliedFilterSnapshot = useMemo(
    () =>
      buildAppliedAdsFilterSnapshot({
        pathname: location.pathname,
        search: location.search,
      }),
    [location.pathname, location.search]
  );
  const canSaveAppliedFilters = hasMeaningfulAppliedFilters(appliedFilterSnapshot);

  const [viewMode, setViewMode] = useState<AdsViewModeEnum>(AdsViewModeEnum.List);
  const [changedFilter, setChangeFilter] = useState<boolean>(false);
  const sortingFields = ['date', /* 'popular', */ 'price' /*'fee'*/];
  const [openSelectionDialog, setOpenSelectionDialog] = useState(false);
  const [openSaveFilterDialog, setOpenSaveFilterDialog] = useState(false);
  const [saveFilterDialogState, setSaveFilterDialogState] = useState<SaveFilterDialogState>({
    status: 'form',
  });
  const isMountedRef = useRef(true);
  const saveFilterRequestInFlightRef = useRef(false);
  const saveFilterSuccessActionHandledRef = useRef(false);
  const pendingClientRequestRouteStateRef = useRef<StorefrontClientRequestRouteState | null>(null);
  const [selectedListing, setSelectedListing] = useState<{ id: string; titleText: string } | null>(
    null
  );
  const [topAgents, setTopAgents] = useState<TopAgentListItem[]>([]);
  const [topAgentsStatus, setTopAgentsStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>(
    'idle'
  );

  const {
    locations,
    setLocations,
    minimizedLocations,
    setMinimizedLocations,
    selectedLocation,
    setSelectedLocation,
    inputAddressValue,
    setInputAddressValue,
    ...locationProps
  } = useLocationAutocomplete({ defaultValue: addressInputValue });
  const {
    ads,
    filteringObj,
    hasCompletedInitialLoad,
    initialLoadError,
    isLoading: isLoadingGetAds,
    paginationObj,
    pageCount,
    retry: handleRetry,
    selectedMarker,
    setFilteringObj,
    setSelectedMarker,
    sortingObj,
  } = useAdsStorefrontData({
    changeQueryParams,
    defaultSortingField: sortingFields[0],
    enableAmbassadorPromotion,
    enableTopAgentFilter,
    isRent: !!isRent,
    queryParamsStr,
    rowsPerPage: adsPerPage,
    setMinimizedLocations,
  });
  const selectedLocationRef = useRef(selectedLocation);
  const pendingLocationInputValueRef = useRef<string | null>(null);
  const preservedMarkerLocationIdRef = useRef<string | null>(null);
  // MapUi flies to its default viewport when selectedLocation changes to null.
  // Keep its last real LocationModel while a marker is represented only by locationId.
  const [mapSelectedLocation, setMapSelectedLocation] = useState(selectedLocation);

  const { userSettings } = useUserSettings();
  const paginationPresentationObj =
    paginationObj && pageCount !== undefined
      ? {
          ...paginationObj,
          total: pageCount * paginationObj.limit,
        }
      : paginationObj;
  const shouldShowPagination =
    pageCount !== undefined ? pageCount > 1 : (paginationObj?.total ?? 0) > adsPerPage;

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enableTopAgentFilter) return;

    let isActive = true;
    setTopAgentsStatus('loading');

    const loadTopAgents = async () => {
      try {
        const { ok, data } = await adsProvider.getTopAgents();
        if (!isActive) return;

        if (ok && data) {
          setTopAgents(Array.isArray(data.items) ? data.items : []);
          setTopAgentsStatus('loaded');
        } else {
          setTopAgents([]);
          setTopAgentsStatus('error');
        }
      } catch {
        if (!isActive) return;
        setTopAgents([]);
        setTopAgentsStatus('error');
      }
    };

    void loadTopAgents();

    return () => {
      isActive = false;
    };
  }, [enableTopAgentFilter]);

  const updateSelectedLocation = (location: typeof selectedLocation) => {
    selectedLocationRef.current = location;
    preservedMarkerLocationIdRef.current = null;
    setSelectedLocation(location);
    setMapSelectedLocation(location);
  };

  useEffect(() => {
    const pendingLocationInputValue = pendingLocationInputValueRef.current;
    pendingLocationInputValueRef.current = null;
    setInputAddressValue(pendingLocationInputValue ?? addressInputValue);
    const currentLocation = selectedLocationRef.current;
    const isCurrentLocationSelected =
      !!currentLocation && !!queryParams.locationId?.includes(currentLocation.id);
    const preservedMarkerLocationId = preservedMarkerLocationIdRef.current;
    const shouldPreserveMapLocation =
      !!preservedMarkerLocationId && !!queryParams.locationId?.includes(preservedMarkerLocationId);

    if (isCurrentLocationSelected) {
      preservedMarkerLocationIdRef.current = null;
      setMapSelectedLocation(currentLocation);
      return;
    }

    selectedLocationRef.current = null;
    setSelectedLocation(null);

    if (!shouldPreserveMapLocation) {
      preservedMarkerLocationIdRef.current = null;
      setMapSelectedLocation(null);
    }
  }, [addressInputValue, queryParamsStr, setInputAddressValue, setSelectedLocation]);

  const handleChangeFilter = (
    newFilteringObj: AdsFilteringType,
    shouldSendRequest?: boolean,
    options?: AdsFilterChangeOptions
  ) => {
    const normalizedFilteringObj = normalizeAdsFilteringObject(newFilteringObj);

    setFilteringObj(normalizedFilteringObj);
    if (shouldSendRequest) {
      pendingLocationInputValueRef.current = options?.preserveLocationInputValue ?? null;
      const { creator: _creator, handover: _handover, ...rest } = normalizedFilteringObj;

      changeQueryParams(
        stringifyAdsSearchParams({
          ...queryParams,
          ...setAddressParamsInFilteringObj(rest),
          ...getPreservedLocationDisplayParams(queryParams, rest),
          page: 1,
          topAgent:
            !enableTopAgentFilter || options?.clearTopAgent || normalizedFilteringObj.creator
              ? undefined
              : queryParams.topAgent,
        })
      );
    }
    setChangeFilter(true);
  };

  const handleChangeTopAgent = (topAgent: TopAgentFilterValue) => {
    const currentFilteringObj = filteringObj ?? {};
    const nextFilteringObj = topAgent
      ? normalizeAdsFilteringObject({
          ...currentFilteringObj,
          creator: undefined,
          'creator.id': undefined,
          'creator.kind': undefined,
          'creator.name': undefined,
        })
      : currentFilteringObj;
    const { creator: _creator, handover: _handover, ...rest } = nextFilteringObj;

    setFilteringObj(nextFilteringObj);
    changeQueryParams(
      stringifyAdsSearchParams({
        ...queryParams,
        ...setAddressParamsInFilteringObj(rest),
        ...getPreservedLocationDisplayParams(queryParams, rest),
        'creator.id': topAgent ? undefined : queryParams['creator.id'],
        'creator.kind': topAgent ? undefined : queryParams['creator.kind'],
        'creator.name': topAgent ? undefined : queryParams['creator.name'],
        page: 1,
        topAgent,
      })
    );
    setChangeFilter(true);
  };

  const handleChangeSort = (newSortingObj: AdsSortingType) =>
    changeQueryParams(
      stringifyAdsSearchParams({
        ...queryParams,
        ...newSortingObj,
        topAgent: enableTopAgentFilter ? queryParams.topAgent : undefined,
      })
    );

  const handleChangePage = (page: number) => {
    window.scrollTo({ top: 0, behavior: 'auto' });
    changeQueryParams(
      stringifyAdsSearchParams({
        ...queryParams,
        page,
        topAgent: enableTopAgentFilter ? queryParams.topAgent : undefined,
      })
    );
  };

  const onLocationClick = (locationId: string) => {
    const location =
      selectedLocation?.id === locationId
        ? selectedLocation
        : locations.find(({ id }) => id === locationId) ?? null;
    const listing = ads?.find((item) => item.locationId === locationId);
    const locationName = location?.[location.kind]?.trim() || listing?.nameEn?.trim() || '';
    const locationAddress = listing?.addressEn?.trim() || locationName;
    const displayName = locationName || locationAddress;

    if (location) {
      updateSelectedLocation(location);
    } else {
      preservedMarkerLocationIdRef.current = locationId;
      selectedLocationRef.current = null;
      setSelectedLocation(null);
    }
    setInputAddressValue(displayName);
    handleChangeFilter(
      {
        ...filteringObj,
        locationId,
        address: displayName
          ? [
              {
                addressEn: locationAddress,
                nameEn: displayName,
              },
            ]
          : [],
      },
      true
    );
    setSelectedMarker(locationId);
  };

  const handleOpenSelectionDialog = (id: string, titleText: string) => {
    setSelectedListing({ id, titleText });
    setOpenSelectionDialog(true);
  };

  const handleCloseSelectionDialog = () => {
    setOpenSelectionDialog(false);
    setSelectedListing(null);
  };

  const handleCloseSaveFilterDialog = () => {
    if (saveFilterRequestInFlightRef.current) return;

    pendingClientRequestRouteStateRef.current = null;
    saveFilterSuccessActionHandledRef.current = false;
    setOpenSaveFilterDialog(false);
  };

  const handleOpenSaveFilterDialog = () => {
    if (!canSaveAppliedFilters) return;

    pendingClientRequestRouteStateRef.current = null;
    saveFilterSuccessActionHandledRef.current = false;
    setSaveFilterDialogState({ status: 'form' });
    setOpenSaveFilterDialog(true);
  };

  const handleSaveFilterDialogExited = () => {
    if (isMountedRef.current) {
      setSaveFilterDialogState({ status: 'form' });
    }
  };

  const handleSaveFilterSuccessAction = () => {
    if (
      saveFilterDialogState.status !== 'success-combined' ||
      saveFilterSuccessActionHandledRef.current
    ) {
      return;
    }

    const routeState = pendingClientRequestRouteStateRef.current;
    if (!routeState) return;

    saveFilterSuccessActionHandledRef.current = true;
    pendingClientRequestRouteStateRef.current = null;
    setOpenSaveFilterDialog(false);
    navigate('/client-request/create', { state: routeState });
  };

  const handleContinueSaveFilter = async ({
    saveAsClientRequest,
    saveAsFilter,
    title: rawTitle,
  }: SaveFilterResult) => {
    const title = rawTitle.trim();
    const snapshot = appliedFilterSnapshot;

    if (!hasMeaningfulAppliedFilters(snapshot)) return;

    const clientRequestRouteState = saveAsClientRequest
      ? buildStorefrontClientRequestRouteState({
          returnTo: `${location.pathname}${location.search}`,
          snapshot,
          title,
        })
      : null;

    if (!saveAsFilter) {
      if (clientRequestRouteState) {
        navigate('/client-request/create', { state: clientRequestRouteState });
      }
      return;
    }

    if (saveFilterRequestInFlightRef.current) return;

    saveFilterRequestInFlightRef.current = true;
    saveFilterSuccessActionHandledRef.current = false;
    pendingClientRequestRouteStateRef.current = null;
    setSaveFilterDialogState({ status: 'saving' });

    try {
      const response = await savedFilterProvider.createSavedFilter({
        name: title,
        criteriaVersion: 1,
        criteria: snapshot,
      });

      if (!isMountedRef.current) return;

      if (!response.ok || response.status !== 201) {
        dispatch(
          appSetNotification(
            NotificationTypeEnum.Error,
            response.message || 'Unable to save filter',
            response.status
          )
        );
        setSaveFilterDialogState({ status: 'form' });
        return;
      }

      if (clientRequestRouteState) {
        pendingClientRequestRouteStateRef.current = clientRequestRouteState;
        setSaveFilterDialogState({
          status: 'success-combined',
          title,
        });
      } else {
        setSaveFilterDialogState({
          status: 'success-save-only',
          title,
        });
      }
    } catch (error) {
      if (!isMountedRef.current) return;

      dispatch(
        appSetNotification(
          NotificationTypeEnum.Error,
          error instanceof Error ? error.message : 'Unable to save filter'
        )
      );
      setSaveFilterDialogState({ status: 'form' });
    } finally {
      saveFilterRequestInFlightRef.current = false;
    }
  };

  if (!hasCompletedInitialLoad) {
    return <Loader />;
  }

  const renderAdsGrid = (items: AdModel[]) => (
    <div
      className={classnames({
        [styles.mapMode]: viewMode === AdsViewModeEnum.Map,
        [styles.adsList]: viewMode === AdsViewModeEnum.List,
      })}
    >
      {items.map((item) => (
        <AdsListItem
          key={item.id}
          type='main'
          item={item}
          enableTopAgentHighlight={enableAmbassadorPromotion}
          filteringObj={filteringObj}
          isPromoted={enableAmbassadorPromotion && item.isPromoted === true}
          userId={user.id}
          tariffPlan={user.tariffPlan?.name}
          onSelectionClick={handleOpenSelectionDialog}
        />
      ))}
    </div>
  );

  return (
    <div aria-busy={isLoadingGetAds} className={styles.adsContainer}>
      {filteringObj && (
        <AdsFilters
          user={user}
          filteringObj={filteringObj}
          onChangeFilters={handleChangeFilter}
          locations={locations}
          setLocations={setLocations}
          selectedLocation={selectedLocation}
          setSelectedLocation={updateSelectedLocation}
          setChangeFilter={setChangeFilter}
          isRent={isRent}
          userSettings={userSettings}
          setInputAddressValue={setInputAddressValue}
          inputAddressValue={inputAddressValue}
          enableTopAgentFilter={enableTopAgentFilter}
          onTopAgentChange={handleChangeTopAgent}
          topAgent={queryParams.topAgent}
          topAgents={topAgents}
          topAgentsError={topAgentsStatus === 'error'}
          topAgentsLoaded={topAgentsStatus === 'loaded'}
          topAgentsLoading={topAgentsStatus === 'loading'}
          {...locationProps}
        />
      )}

      <div className={styles.templateAdsContainer}>
        <div className={styles.listContainer}>
          {sortingObj && (
            <AdsListControls
              adminAction={
                user.kind === UserKindEnum.Admin ? <ResetPublicationTimerAction /> : undefined
              }
              viewMode={viewMode}
              setViewMode={setViewMode}
              sortingFields={sortingFields}
              sortingObj={sortingObj}
              onChangeSort={handleChangeSort}
              resultsCount={
                !initialLoadError && paginationObj ? (
                  <AdsResultsCountBadge total={paginationObj.total} />
                ) : undefined
              }
              saveFilterAction={
                user.kind !== UserKindEnum.Admin ? (
                  <MyButton
                    data={{
                      buttonName: 'Save filter',
                      buttonType: 'button',
                      buttonsIcon: true,
                      customWidth: 'auto',
                      icon: <SaveFilterIcon />,
                      iconPosition: 'start',
                      size: 'medium',
                      variant: 'contained',
                    }}
                    disabled={!canSaveAppliedFilters}
                    onClick={handleOpenSaveFilterDialog}
                  />
                ) : undefined
              }
            />
          )}
          <div
            className={classnames(styles.content, {
              [styles.mapContent]: viewMode === AdsViewModeEnum.Map,
            })}
          >
            {initialLoadError ? (
              <div className={styles.loadError} role='alert'>
                <div className={styles.loadErrorTitle}>Unable to load listings</div>
                <div className={styles.loadErrorDescription}>{initialLoadError}</div>
                <MyButton
                  data={{
                    buttonName: 'Retry',
                    buttonType: 'button',
                    customWidth: '166px',
                    variant: 'contained',
                  }}
                  disabled={isLoadingGetAds}
                  onClick={handleRetry}
                />
              </div>
            ) : !ads?.length ? (
              <EmptyList changedFilter={changedFilter} />
            ) : (
              renderAdsGrid(ads)
            )}
            {viewMode === AdsViewModeEnum.Map && (
              <div className={styles.mapPanel}>
                <MapUi
                  height='var(--catalog-map-height, 70vh)'
                  selectedLocation={mapSelectedLocation}
                  minimizedLocations={minimizedLocations}
                  onLocationClick={onLocationClick}
                  selectedMarker={selectedMarker}
                />
              </div>
            )}
          </div>
          {shouldShowPagination && paginationPresentationObj ? (
            <Pagination paginationObj={paginationPresentationObj} onChangePage={handleChangePage} />
          ) : null}
        </div>
      </div>
      {selectedListing && (
        <DialogSelection
          userId={user.id}
          open={openSelectionDialog}
          onClose={handleCloseSelectionDialog}
          listingId={selectedListing.id}
          titleText={selectedListing.titleText}
        />
      )}
      <SaveFilterDialog
        onClose={handleCloseSaveFilterDialog}
        onContinue={handleContinueSaveFilter}
        onExited={handleSaveFilterDialogExited}
        onSuccessAction={handleSaveFilterSuccessAction}
        open={openSaveFilterDialog}
        state={saveFilterDialogState}
      />
      {isLoadingGetAds && (
        <div aria-label='Loading ads' className={styles.loadingOverlay} role='status'>
          <Loader />
        </div>
      )}
    </div>
  );
};
